import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ShieldCheck, ShieldAlert, ShieldQuestion } from "lucide-react";

const ACTION_STYLES = {
  login: "bg-slate-100 text-slate-700",
  login_failure: "bg-rose-50 text-rose-700",
  logout: "bg-slate-100 text-slate-700",
  view: "bg-sky-50 text-sky-700",
  create: "bg-emerald-50 text-emerald-700",
  update: "bg-amber-50 text-amber-700",
  protocol_change: "bg-violet-50 text-violet-700",
  lab_order: "bg-sky-50 text-sky-700",
  lab_result: "bg-amber-50 text-amber-700",
  lab_review: "bg-emerald-50 text-emerald-700",
  followup_complete: "bg-emerald-50 text-emerald-700",
  message: "bg-indigo-50 text-indigo-700",
  quick_action: "bg-indigo-50 text-indigo-700",
};

const BASIS_STYLES = {
  TREATMENT: "bg-sky-50 text-sky-700 border-sky-200",
  PAYMENT: "bg-emerald-50 text-emerald-700 border-emerald-200",
  OPERATIONS: "bg-slate-50 text-slate-600 border-slate-200",
  SECURITY: "bg-amber-50 text-amber-700 border-amber-200",
  PATIENT_ACCESS: "bg-violet-50 text-violet-700 border-violet-200",
  AUTHORIZATION: "bg-indigo-50 text-indigo-700 border-indigo-200",
};

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [integrity, setIntegrity] = useState(null);

  useEffect(() => {
    api.get("/audit-logs").then((r) => { setLogs(r.data); setLoading(false); }).catch(() => setLoading(false));
    api.get("/audit-logs/verify").then((r) => setIntegrity(r.data)).catch(() => setIntegrity(null));
  }, []);

  const IntegrityBadge = () => {
    if (!integrity) {
      return (
        <span data-testid="audit-integrity-badge" className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 bg-slate-50 border border-slate-200 rounded-full px-3 py-1.5">
          <ShieldQuestion className="w-3.5 h-3.5" /> Integrity unknown
        </span>
      );
    }
    if (integrity.intact) {
      return (
        <span data-testid="audit-integrity-badge" className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5">
          <ShieldCheck className="w-3.5 h-3.5" /> Chain verified · {integrity.total} entries
        </span>
      );
    }
    return (
      <span data-testid="audit-integrity-badge" className="inline-flex items-center gap-1.5 text-xs font-medium text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-3 py-1.5">
        <ShieldAlert className="w-3.5 h-3.5" /> Tampering detected at #{integrity.broken_at_seq}
      </span>
    );
  };

  return (
    <div className="space-y-6" data-testid="audit-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Audit Log</h1>
          <p className="text-sm text-muted-foreground mt-1">Tamper-evident, hash-chained record of access and changes for HIPAA compliance.</p>
        </div>
        <div className="flex items-center gap-2">
          <IntegrityBadge />
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-secondary/50">
              <th className="text-left font-medium text-muted-foreground px-4 py-3">Time</th>
              <th className="text-left font-medium text-muted-foreground px-4 py-3">User</th>
              <th className="text-left font-medium text-muted-foreground px-4 py-3">Action</th>
              <th className="text-left font-medium text-muted-foreground px-4 py-3">Basis</th>
              <th className="text-left font-medium text-muted-foreground px-4 py-3">Detail</th>
            </tr>
          </thead>
          <tbody data-testid="audit-table-body">
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Loading audit log…</td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">No audit entries.</td></tr>
            ) : (
              logs.map((l) => (
                <tr key={l.id} className="border-b border-border last:border-0 hover:bg-secondary/40 transition-colors">
                  <td className="px-4 py-3 text-muted-foreground tabular-nums whitespace-nowrap">{new Date(l.timestamp).toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <span className="font-medium">{l.user_name}</span>
                    <span className="block text-xs text-muted-foreground capitalize">{(l.user_role || "").replace("_", " ")}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex rounded-md px-2 py-0.5 text-xs font-medium ${ACTION_STYLES[l.action] || "bg-slate-100 text-slate-700"}`}>
                      {l.action.replace("_", " ")}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {l.privacy_basis ? (
                      <span className={`inline-flex rounded border px-2 py-0.5 text-[11px] font-medium ${BASIS_STYLES[l.privacy_basis] || "bg-slate-50 text-slate-600 border-slate-200"}`}>
                        {l.privacy_basis.replace("_", " ")}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{l.detail}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
